import React, { useEffect, useState, useRef, Fragment } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, MicIcon, ImageIcon, PaperclipIcon, ArrowUpIcon, CarIcon, HomeIcon, BriefcaseIcon, TagIcon, TruckIcon, SmartphoneIcon, HistoryIcon, ChevronRightIcon, XIcon, PlusIcon, TrendingDownIcon, ThumbsUpIcon, AlertTriangleIcon, WrenchIcon, ShoppingBagIcon, GraduationCapIcon, PlaneIcon, UserIcon } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { AIResponse } from '../components/AIResponse';
import { ListingCard, ListingCardProps } from '../components/ListingCard';
import { ListingCarousel } from '../components/ListingCarousel';
import { useBusiness, BusinessType } from '../components/BusinessContext';
import { useAuth } from '../components/AuthContext';
// --- Seller intent detection ---
type SellIntent = {
  tier: 'individual' | 'business' | 'enterprise';
  type: BusinessType;
};
const detectSellIntent = (text: string): SellIntent | null => {
  const t = text.toLowerCase();
  const sellish = /\b(sell|selling|list|listing|post|inventory|stock|dealer|agency|recruit|hire|hiring|jobs?|shop|store|business|merchant|products?|services?|manage (leads|inquiries|properties|listings))\b/.test(t);
  if (!sellish) return null;
  // Business type
  let type: BusinessType = 'generic';
  if (/\b(car|cars|vehicle|vehicles|auto|motors?|dealership|dealer)\b/.test(t)) type = 'car';else if (/\b(real estate|property|properties|villa|apartment|agency|broker)\b/.test(t)) type = 'realestate';else if (/\b(recruit|hiring|hire|jobs?|candidates?|talent)\b/.test(t)) type = 'recruitment';else if (/\b(service|services|provider|cleaning|moving|maintenance)\b/.test(t)) type = 'service';else if (/\b(products?|furniture|electronics|goods|items?|inventory|shop|store)\b/.test(t)) type = 'product';
  // Enterprise signals: dealer / agency / recruiter / company / multiple
  const enterprise = /\b(dealer|dealership|agency|recruiter|recruitment|company|enterprise|fleet|represent)\b/.test(t);
  // Business signals: ongoing selling, inventory, multiple, manage
  const business = /\b(business|shop|store|inventory|stock|products?|services?|multiple|many|several|bulk|manage (leads|inquiries|listings))\b/.test(t);
  // Individual: "sell my <single item>"
  const individual = /\bsell(ing)?\s+(my|a|an|this|one)\s+\w+/.test(t);
  let tier: SellIntent['tier'] = 'individual';
  if (enterprise) tier = 'enterprise';else if (business) tier = 'business';else if (individual) tier = 'individual';else tier = 'business'; // generic seller phrasing leans business
  return {
    tier,
    type
  };
};
const BUSINESS_LABEL_INLINE: Record<BusinessType, string> = {
  car: 'vehicle inventory',
  realestate: 'property listings',
  recruitment: 'job postings and candidates',
  service: 'services',
  product: 'product inventory',
  generic: 'listings'
};
const suggestedPrompts = [{
  text: 'Find me a Prado under AED 90,000',
  icon: CarIcon
}, {
  text: "Need a villa near my children's school",
  icon: HomeIcon
}, {
  text: 'Help me find a job',
  icon: BriefcaseIcon
}, {
  text: 'Find a moving company tomorrow',
  icon: WrenchIcon
}, {
  text: 'Compare iPhone 16 Pro prices',
  icon: SmartphoneIcon
}, {
  text: 'Find flights to Istanbul',
  icon: PlaneIcon
}];
const categoryRibbonItems = [{
  label: 'Cars',
  icon: CarIcon,
  prompt: 'Find me a car'
}, {
  label: 'Properties',
  icon: HomeIcon,
  prompt: 'Find me a property'
}, {
  label: 'Jobs',
  icon: BriefcaseIcon,
  prompt: 'Help me find a job'
}, {
  label: 'Services',
  icon: WrenchIcon,
  prompt: 'I need a service'
}, {
  label: 'Products',
  icon: ShoppingBagIcon,
  prompt: "I'm looking for a product"
}, {
  label: 'Moving',
  icon: TruckIcon,
  prompt: 'Find me a moving company'
}, {
  label: 'Education',
  icon: GraduationCapIcon,
  prompt: 'Find education or courses'
}, {
  label: 'Travel',
  icon: PlaneIcon,
  prompt: 'Plan my travel'
}, {
  label: 'Professionals',
  icon: UserIcon,
  prompt: 'Find a professional'
}];
const recentSearches = ['Prado under AED 90,000', 'Villa near GEMS School', 'ERP Manager job in Dubai', 'iPhone 16 Pro price'];
// Mock data for search results
const mockInsights = [{
  title: 'Average Price',
  value: 'AED 84,000',
  type: 'neutral' as const,
  icon: TrendingDownIcon
}, {
  title: 'Best Value',
  value: 'Prado GXR 2018',
  type: 'positive' as const,
  icon: ThumbsUpIcon,
  targetId: '1'
}, {
  title: 'Lowest Mileage',
  value: 'Prado VX 2019',
  type: 'positive' as const,
  icon: SparklesIcon,
  targetId: '4'
}, {
  title: 'Risk Alerts',
  value: '4 imported detected',
  type: 'warning' as const,
  icon: AlertTriangleIcon
}];
const propertyInsights = [{
  title: 'Average Price',
  value: 'AED 2.8M',
  type: 'neutral' as const,
  icon: TrendingDownIcon
}, {
  title: 'Best Value',
  value: '5BR Villa — Mudon',
  type: 'positive' as const,
  icon: ThumbsUpIcon,
  targetId: 'p3'
}, {
  title: 'Closest to School',
  value: 'Dubai Hills · 3 min',
  type: 'positive' as const,
  icon: HomeIcon,
  targetId: 'p5'
}, {
  title: 'Note',
  value: '2 listings under offer',
  type: 'warning' as const,
  icon: AlertTriangleIcon
}];
type SearchCategory = 'cars' | 'property';
const detectCategory = (text: string): SearchCategory => {
  const t = text.toLowerCase();
  const propertyHints = ['villa', 'apartment', 'apt', 'home', 'house', 'property', 'bedroom', 'br ', 'townhouse', 'flat', 'rent', 'school', 'studio'];
  return propertyHints.some(h => t.includes(h)) ? 'property' : 'cars';
};
const CAR_IMG_A = 'https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=800';
const CAR_IMG_B = 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=800';
const CAR_IMG_C = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&q=80&w=800';
// Full result set (label says 27; we show a representative slice in the carousel)
const mockListings = [{
  id: '1',
  title: 'Toyota Prado GXR 2018',
  price: 'AED 82,000',
  location: 'Al Quoz, Dubai',
  images: [CAR_IMG_A, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '112,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 98,
  sellerType: 'Verified Business' as const
}, {
  id: '2',
  title: 'Toyota Prado TXL 2019',
  price: 'AED 89,000',
  location: 'Abu Shagara, Sharjah',
  images: [CAR_IMG_B, CAR_IMG_A],
  specs: [{
    label: 'Year',
    value: '2019'
  }, {
    label: 'Mileage',
    value: '85,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Silver'
  }],
  matchScore: 94,
  sellerType: 'Individual' as const
}, {
  id: '3',
  title: 'Toyota Prado VXR 2017',
  price: 'AED 78,000',
  location: 'Motor City, Dubai',
  images: [CAR_IMG_C, CAR_IMG_A],
  specs: [{
    label: 'Year',
    value: '2017'
  }, {
    label: 'Mileage',
    value: '145,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Black'
  }],
  matchScore: 88,
  sellerType: 'Dealer' as const
}, {
  id: '4',
  title: 'Toyota Prado VX 2019',
  price: 'AED 87,500',
  location: 'Mussafah, Abu Dhabi',
  images: [CAR_IMG_A, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2019'
  }, {
    label: 'Mileage',
    value: '64,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 92,
  sellerType: 'Verified Business' as const
}, {
  id: '5',
  title: 'Toyota Prado GXR 2016',
  price: 'AED 69,000',
  location: 'Al Nahda, Sharjah',
  images: [CAR_IMG_B, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2016'
  }, {
    label: 'Mileage',
    value: '168,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Grey'
  }],
  matchScore: 81,
  sellerType: 'Individual' as const
}, {
  id: '6',
  title: 'Toyota Prado TXL 2018',
  price: 'AED 84,900',
  location: 'Deira, Dubai',
  images: [CAR_IMG_C, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '98,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 90,
  sellerType: 'Dealer' as const
}, {
  id: '7',
  title: 'Toyota Prado VX 2018',
  price: 'AED 86,000',
  location: 'JVC, Dubai',
  images: [CAR_IMG_A, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '77,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Beige'
  }],
  matchScore: 89,
  sellerType: 'Verified Business' as const
}, {
  id: '8',
  title: 'Toyota Prado GXR 2017',
  price: 'AED 74,500',
  location: 'Al Ain',
  images: [CAR_IMG_B, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2017'
  }, {
    label: 'Mileage',
    value: '132,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Silver'
  }],
  matchScore: 83,
  sellerType: 'Individual' as const
}];
// The 3 strong matches after the user narrows their request
const narrowedListings = [mockListings[3], mockListings[6], mockListings[0]];
// ---- Real-estate (property) dataset ----
const HOME_IMG_A = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=800';
const HOME_IMG_B = 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&q=80&w=800';
const HOME_IMG_C = 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80&w=800';
const propertyListings: ListingCardProps[] = [{
  id: 'p1',
  title: '4BR Villa — Arabian Ranches',
  price: 'AED 3.2M',
  location: 'Arabian Ranches, Dubai',
  images: [HOME_IMG_A, HOME_IMG_C],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '5 Bath'
  }, {
    label: 'Area',
    value: '3,400 sqft'
  }, {
    label: 'School',
    value: '5 min'
  }],
  matchScore: 97,
  sellerType: 'Verified Business' as const
}, {
  id: 'p2',
  title: '3BR Townhouse — Town Square',
  price: 'AED 1.9M',
  location: 'Town Square, Dubai',
  images: [HOME_IMG_B, HOME_IMG_A],
  specs: [{
    label: 'Bedrooms',
    value: '3 BR'
  }, {
    label: 'Bathrooms',
    value: '4 Bath'
  }, {
    label: 'Area',
    value: '2,100 sqft'
  }, {
    label: 'School',
    value: '8 min'
  }],
  matchScore: 93,
  sellerType: 'Dealer' as const
}, {
  id: 'p3',
  title: '5BR Villa — Mudon',
  price: 'AED 4.1M',
  location: 'Mudon, Dubai',
  images: [HOME_IMG_C, HOME_IMG_B],
  specs: [{
    label: 'Bedrooms',
    value: '5 BR'
  }, {
    label: 'Bathrooms',
    value: '6 Bath'
  }, {
    label: 'Area',
    value: '4,200 sqft'
  }, {
    label: 'School',
    value: '4 min'
  }],
  matchScore: 95,
  sellerType: 'Verified Business' as const
}, {
  id: 'p4',
  title: '4BR Villa — The Springs',
  price: 'AED 2.8M',
  location: 'The Springs, Dubai',
  images: [HOME_IMG_A, HOME_IMG_B],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '4 Bath'
  }, {
    label: 'Area',
    value: '2,900 sqft'
  }, {
    label: 'School',
    value: '6 min'
  }],
  matchScore: 91,
  sellerType: 'Individual' as const
}, {
  id: 'p5',
  title: '3BR Apartment — Dubai Hills',
  price: 'AED 2.3M',
  location: 'Dubai Hills Estate',
  images: [HOME_IMG_C, HOME_IMG_A],
  specs: [{
    label: 'Bedrooms',
    value: '3 BR'
  }, {
    label: 'Bathrooms',
    value: '3 Bath'
  }, {
    label: 'Area',
    value: '1,800 sqft'
  }, {
    label: 'School',
    value: '3 min'
  }],
  matchScore: 89,
  sellerType: 'Dealer' as const
}, {
  id: 'p6',
  title: '4BR Villa — Reem Community',
  price: 'AED 2.6M',
  location: 'Reem, Dubai',
  images: [HOME_IMG_B, HOME_IMG_C],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '5 Bath'
  }, {
    label: 'Area',
    value: '3,100 sqft'
  }, {
    label: 'School',
    value: '7 min'
  }],
  matchScore: 88,
  sellerType: 'Verified Business' as const
}];
const propertyNarrowed = [propertyListings[2], propertyListings[0], propertyListings[4]];
type SelectedVehicle = {
  id: string;
  title: string;
};
type ChatMessage = {
  role: 'user' | 'ai';
  content: string;
  // 'search' = first broad result set (carousel)
  // 'narrow_prompt' = follow-up offer to refine the results
  // 'narrowed' = refined 3-result set
  // 'vehicle_answer' = AI answer about specific selected vehicles
  kind?: 'search' | 'narrow_prompt' | 'narrowed' | 'vehicle_answer' | 'seller_offer' | 'seller_simple';
  vehicles?: SelectedVehicle[];
  category?: SearchCategory;
  sellType?: BusinessType;
};
export function Discover() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    activateHub,
    hub
  } = useBusiness();
  const {
    requireAuth
  } = useAuth();
  const isShopMode = !!slug;
  const shopLogo = isShopMode && hub?.shopSlug === slug ? hub.logo : null;
  const shopName = isShopMode && hub?.shopSlug === slug ? hub.name : null;
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [hasNarrowed, setHasNarrowed] = useState(false);
  const [searchCategory, setSearchCategory] = useState<SearchCategory>('cars');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<SelectedVehicle[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const names = Array.from(files).map(f => f.name);
    setAttachments(prev => [...prev, ...names]);
    e.target.value = '';
  };
  const removeAttachment = (idx: number) => setAttachments(prev => prev.filter((_, i) => i !== idx));
  const toggleRecording = () => setIsRecording(r => !r);
  // Keep the latest message + input in view as the conversation grows.
  // Scroll the CONTAINER to its full height (more reliable than scrollIntoView
  // when AI replies add tall content/images that grow height after paint).
  useEffect(() => {
    const scrollToBottom = () => {
      const el = scrollContainerRef.current;
      if (el) el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth'
      });
    };
    const raf = requestAnimationFrame(scrollToBottom);
    const t1 = setTimeout(scrollToBottom, 250);
    const t2 = setTimeout(scrollToBottom, 700);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [chatHistory, isSearching]);
  const scrollToListing = (id: string) => {
    const el = document.getElementById(`listing-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
      el.classList.add('ring-2', 'ring-primary-indigo', 'ring-offset-2');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-primary-indigo', 'ring-offset-2');
      }, 1600);
    }
  };
  const resetConversation = () => {
    setChatHistory([]);
    setHasResults(false);
    setHasNarrowed(false);
    setSelectedVehicles([]);
    setSearchCategory('cars');
    setQuery('');
    setIsSearching(false);
  };
  // Clicking "Discover" in the nav navigates to "/" with a fresh `reset`
  // timestamp — start a brand-new chat even if already on this route.
  useEffect(() => {
    if (location.state && (location.state as {
      reset?: number;
    }).reset) {
      resetConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  // Business seller accepted — require sign-in, then create the workspace
  // and take the user straight into the Business Hub.
  const createBusinessHub = (type: BusinessType) => {
    requireAuth(() => {
      activateHub(type);
      navigate('/business');
    });
  };
  // Business seller declined — fall back to a single listing in chat
  const continueWithOneListing = () => {
    setChatHistory(prev => [...prev, {
      role: 'ai',
      kind: 'seller_simple',
      content: "No problem — let's create a single listing right here. Upload a few photos and tell me the key details, and I'll generate a title, description, and a suggested price for you to review before publishing."
    }]);
  };
  const toggleVehicle = (id: string, title: string) => {
    setSelectedVehicles(prev => prev.some(v => v.id === id) ? prev.filter(v => v.id !== id) : [...prev, {
      id,
      title
    }]);
  };
  // Attach selection state + handler to a listing before rendering
  const withSelection = (listing: ListingCardProps): ListingCardProps => ({
    ...listing,
    selected: selectedVehicles.some(v => v.id === listing.id),
    onToggleSelect: () => toggleVehicle(listing.id, listing.title)
  });
  const runQuery = (text: string) => {
    if (!text.trim() && selectedVehicles.length === 0) return;
    // If vehicles are selected, this is a specific question about them
    if (selectedVehicles.length > 0) {
      const vehicles = selectedVehicles;
      setChatHistory(prev => [...prev, {
        role: 'user',
        content: text || 'Tell me more about these',
        vehicles
      }]);
      setQuery('');
      setSelectedVehicles([]);
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        const names = vehicles.map(v => v.title).join(' and ');
        const answer = vehicles.length > 1 ? `Comparing ${names}: both are GCC-spec and well-maintained. The newer one holds resale value better and has lower mileage, while the other is more affordable upfront. For long-term ownership I'd lean toward the lower-mileage option; for budget, the cheaper one is the smarter buy.` : `About the ${names}: it's a GCC-spec unit with a clean history and fair pricing versus the market. Service records check out and the mileage is reasonable for the year. It's a solid, low-risk choice — happy to dig into anything specific like financing, inspection, or negotiation room.`;
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'vehicle_answer',
          content: answer,
          vehicles
        }]);
      }, 1400);
      return;
    }
    // Detect seller intent — route to a one-time listing or a Business Hub offer
    const sellIntent = detectSellIntent(text);
    if (sellIntent) {
      setChatHistory(prev => [...prev, {
        role: 'user',
        content: text
      }]);
      setQuery('');
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        if (sellIntent.tier === 'individual') {
          // One-time seller — stay in chat, create a simple listing
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'seller_simple',
            content: "Great — upload a few photos and I'll help create the listing. I'll just need a few details: make/model, year, mileage, condition, accident history, GCC specs, and your asking price. Then I'll generate a title, description, and a suggested price for you to review."
          }]);
        } else {
          // Business / enterprise — offer to create a Business Hub
          const intro = sellIntent.tier === 'enterprise' ? `It sounds like you operate a business with multiple listings and customer inquiries. I recommend creating a Business Hub to manage your ${BUSINESS_LABEL_INLINE[sellIntent.type]}, receive leads, track inquiries, manage team access, and get your own shareable shop link to send to customers.` : `It sounds like you're managing multiple ${BUSINESS_LABEL_INLINE[sellIntent.type]}. I can create a Business Hub for you to manage inventory, leads, messages, and analytics in one place. Would you like me to set it up?`;
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'seller_offer',
            content: intro,
            sellType: sellIntent.type
          }]);
        }
      }, 1400);
      return;
    }
    const firstSearch = !hasResults;
    const category = firstSearch ? detectCategory(text) : searchCategory;
    if (firstSearch) setSearchCategory(category);
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: text
    }]);
    setQuery('');
    setIsSearching(true);
    setTimeout(() => {
      setIsSearching(false);
      if (firstSearch) {
        setHasResults(true);
        const searchContent = category === 'property' ? 'I found 18 properties that match. I prioritized location, proximity to schools, community quality, and trusted listings.' : 'I found 27 matching vehicles. I prioritized GCC specs, clean history, lower mileage, and trusted sellers.';
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'search',
          content: searchContent,
          category
        }]);
        // Follow-up narrowing offer ~1s later so the user notices it
        const narrowContent = category === 'property' ? "I can narrow this down — tell me your budget, preferred area, number of bedrooms, or which school matters most, and I'll refine these." : "I can narrow this down for you — just tell me your budget, preferred color, or any other detail, and I'll refine these matches.";
        setTimeout(() => {
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'narrow_prompt',
            content: narrowContent
          }]);
        }, 1000);
      } else {
        setHasNarrowed(true);
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'narrowed',
          category,
          content: "Perfect — based on what you told me, I've narrowed it down to just 3 strong matches that fit your preferences best."
        }]);
      }
    }, 1500);
  };
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    // Fold any attachments into the query context, then clear them
    const attachmentNote = attachments.length > 0 ? ` [Attached: ${attachments.join(', ')}]` : '';
    runQuery(`${query}${attachmentNote}`.trim());
    setAttachments([]);
    setIsRecording(false);
  };
  const handlePromptClick = (prompt: string) => {
    runQuery(prompt);
  };
  return <div className="app-fill-screen flex-1 flex flex-col h-[calc(100vh-57px)] lg:h-screen bg-white relative overflow-hidden" data-id="element-3379" data-mp="15tphlt2b0ub1">
      {/* Hidden file pickers for attachments */}
      <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={handleFilesSelected} className="hidden" data-id="element-3380" data-mp="1emj6mi7jz823" />
      <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.csv,.xlsx,.txt" multiple onChange={handleFilesSelected} className="hidden" data-id="element-3381" data-mp="m4op5uw7y2ex" />
      {/* Background — refined architectural canvas: hairline grid, micro texture,
          concentric signal arcs. Deliberately no illustrated icons. */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true" data-id="element-3382" data-mp="2m5d6jygegw2n">
        {/* Micro dot texture — gives the white surface depth up close */}
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(67, 56, 202, 0.055) 1px, transparent 0)',
        backgroundSize: '26px 26px'
      }} data-id="element-3383" data-mp="2vzc5kol6fzqd" />

        {/* Category marks — hairline icons set in frosted tiles, composed along the
            margins so the centre column always stays clear */}
        {[{
        Icon: CarIcon,
        cls: 'top-[15%] left-[4%] w-[78px] h-[78px]',
        r: -8,
        d: 0
      }, {
        Icon: HomeIcon,
        cls: 'top-[44%] left-[8%] w-[58px] h-[58px]',
        r: 6,
        d: 1.4
      }, {
        Icon: BriefcaseIcon,
        cls: 'bottom-[13%] left-[5%] w-[68px] h-[68px]',
        r: -5,
        d: 2.2
      }, {
        Icon: GraduationCapIcon,
        cls: 'bottom-[34%] left-[14%] w-[46px] h-[46px]',
        r: 9,
        d: 3
      }, {
        Icon: ShoppingBagIcon,
        cls: 'top-[13%] right-[5%] w-[64px] h-[64px]',
        r: 7,
        d: 0.8
      }, {
        Icon: PlaneIcon,
        cls: 'top-[43%] right-[3.5%] w-[78px] h-[78px]',
        r: -6,
        d: 1.8
      }, {
        Icon: WrenchIcon,
        cls: 'bottom-[15%] right-[7%] w-[58px] h-[58px]',
        r: 5,
        d: 2.6
      }, {
        Icon: SmartphoneIcon,
        cls: 'bottom-[36%] right-[15%] w-[46px] h-[46px]',
        r: -9,
        d: 3.4
      }].map(({
        Icon,
        cls,
        r,
        d
      }, i) => <motion.div key={i} className={`hidden lg:flex absolute ${cls} items-center justify-center rounded-2xl border border-primary-indigo/[0.09] bg-white/55 backdrop-blur-[2px] shadow-[0_12px_32px_rgba(11,18,32,0.045)]`} style={{
        rotate: r
      }} animate={{
        y: [0, -8, 0]
      }} transition={{
        duration: 14 + i % 5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: d
      }} data-id="element-3384" data-mp="1iluc0cuvqov7" data-mpi={i}>
            <Icon className="w-1/2 h-1/2 text-primary-indigo/[0.22]" strokeWidth={1} data-id="element-3385" data-mp="pvvrmurmrfwv" />
          </motion.div>)}

        {/* Ambient light from the top, then a white vignette so edges stay clean */}
        <div className="absolute inset-x-0 top-0 h-[60%]" style={{
        background: 'radial-gradient(65% 100% at 50% 0%, rgba(67, 56, 202, 0.055), transparent 72%)'
      }} data-id="element-3386" data-mp="2k70w21ajjwd9" />
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(125% 95% at 50% 42%, transparent 38%, rgba(255, 255, 255, 0.92) 100%)'
      }} data-id="element-3387" data-mp="8m099q5wxoy3" />

      </div>

      {/* Chat toolbar — always visible above the thread (never scrolls away) */}
      {(hasResults || isSearching || chatHistory.length > 0) && <div className="flex-shrink-0 px-4 lg:px-12 pt-4 pb-3 relative z-30 bg-white/60 backdrop-blur-md after:content-[''] after:absolute after:inset-x-0 after:top-full after:h-6 after:bg-gradient-to-b after:from-white/60 after:to-transparent after:pointer-events-none" data-id="element-3388" data-mp="s9r8pdk05yuf">
          <div className="max-w-6xl mx-auto w-full" data-id="element-3389" data-mp="1182u9160i6g0">
            <button onClick={resetConversation} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm hover:border-primary-indigo hover:text-primary-indigo transition-colors" data-id="element-3390" data-mp="381ojjy6se82k">
              <PlusIcon className="w-4 h-4" data-id="element-3391" data-mp="27z74btz59g5d" />
              New request
            </button>
          </div>
        </div>}

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden px-4 lg:px-12 pb-24 lg:pb-8 pt-2 lg:pt-0 relative z-10" data-id="element-3392" data-mp="235kusaoa1sds">
        <div className="max-w-6xl mx-auto w-full h-full flex flex-col min-w-0" data-id="element-3393" data-mp="1taaym3kk8f7x">
          {!hasResults && !isSearching && chatHistory.length === 0 ?
        // Initial State (Hero)
        <div className="flex-1 flex flex-col items-center justify-center py-6" data-id="element-3394" data-mp="1k7rxpp0c4cob">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="w-full max-w-6xl flex flex-col items-center relative z-10" data-id="element-3395" data-mp="1fa02al0he8fd">
                {/* Top Icon / Logo */}
                <div className="relative mb-4" data-id="element-3396" data-mp="3h5670iajvx4r">
                  <div className="absolute -inset-3 border border-primary-indigo/[0.12] rounded-full" data-id="element-3397" data-mp="3m523zxktyoat" />
                  <div className="absolute -inset-7 border border-primary-indigo/[0.07] rounded-full" data-id="element-3398" data-mp="3qy4xk5plxxr0" />
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-white rounded-full flex items-center justify-center shadow-soft border border-slate-100 relative z-10 overflow-hidden" data-id="element-3399" data-mp="3vulnn5gdam6q">
                    {isShopMode && shopLogo ? <img src={shopLogo} alt={shopName || 'Shop'} className="w-full h-full object-cover" data-id="element-3400" data-mp="3392jf7u6mn19" /> : <SparklesIcon className="w-8 h-8 lg:w-10 lg:h-10 text-primary-indigo" data-id="element-3401" data-mp="ngmqw1lkmws6" />}
                  </div>
                </div>

                {/* Main Heading */}
                <div className="text-center space-y-2.5 mb-6" data-id="element-3402" data-mp="2oi2u3xijdxn2">
                  {isShopMode ? <>
                      <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-dark-navy tracking-tight" data-id="element-3403" data-mp="2ji6nrrmd9u4c">
                        Welcome to{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-3404" data-mp="2sk640hpk4u68">
                          {shopName || 'Our Shop'}
                        </span>
                      </h1>
                      <p className="text-slate-500 text-sm lg:text-base max-w-xl mx-auto leading-relaxed" data-id="element-3405" data-mp="20trxj6jvik4l">
                        Ask our AI assistant anything about our inventory,
                        pricing, or services.
                      </p>
                    </> : <>
                      <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-dark-navy tracking-tight" data-id="element-3406" data-mp="1m29gr6j6wlk6">
                        Ask Talab{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-3407" data-mp="3srupaolwev2e">
                          Anything
                        </span>
                      </h1>
                      <p className="text-slate-500 text-sm lg:text-base max-w-xl mx-auto leading-relaxed" data-id="element-3408" data-mp="3jspt3oxt7dte">
                        Cars, properties, jobs, services, products and more.
                        <br data-id="element-3409" data-mp="gag2j79jg3k4" />
                        Talab understands your needs, compares options, and
                        helps you make better decisions.
                      </p>
                    </>}
                </div>

                {/* Category Ribbon — full width, evenly distributed, no scroll */}
                <div className="w-full max-w-5xl mb-6" data-id="element-3410" data-mp="b9s1rtvic8cp">
                  <div className="flex items-stretch w-full p-1.5 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-3411" data-mp="2r2hjwml943qy">
                    {categoryRibbonItems.map((item, idx) => {
                  const Icon = item.icon;
                  return <Fragment key={item.label} data-id="element-3412" data-mp="3bdblm1py99gg" data-mpi={idx}>
                          <button onClick={() => {
                      setQuery(item.prompt + ' ');
                      textareaRef.current?.focus();
                    }} className="flex-1 min-w-0 flex flex-col items-center justify-center gap-2 px-1 py-2.5 rounded-xl text-slate-500 hover:bg-indigo-50/70 hover:text-primary-indigo transition-colors duration-150 ease-out group relative" data-id="element-3413" data-mp="36jz1o4w7tqu1">
                            <Icon className="w-[18px] h-[18px] text-slate-400 group-hover:text-primary-indigo transition-colors duration-150 ease-out" strokeWidth={1.5} data-id="element-3414" data-mp="3l8dd347p6nm7" />
                            <span className="text-[10px] font-semibold uppercase tracking-[0.12em] truncate max-w-full" data-id="element-3415" data-mp="24vok21xlglua">
                              {item.label}
                            </span>
                          </button>
                          {idx < categoryRibbonItems.length - 1 && <div className="w-px self-center h-7 bg-slate-100 flex-shrink-0" data-id="element-3416" data-mp="24pxlp083mn67" />}
                        </Fragment>;
                })}
                  </div>
                </div>

                {/* AI Input Area */}
                <div className="w-full max-w-3xl bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 p-2 mb-8 relative group transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] z-10" data-id="element-3417" data-mp="2ih2hfr8civb9">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-indigo/20 to-ai-purple/20 rounded-3xl blur opacity-0 group-focus-within:opacity-100 transition duration-500" data-id="element-3418" data-mp="2sfs19pjsypu5" />
                  <div className="relative bg-white rounded-[22px] p-5 flex flex-col gap-3" data-id="element-3419" data-mp="y0xva2ldtbbe">
                    <textarea ref={textareaRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }} placeholder={isShopMode ? `Ask ${shopName || 'us'} anything...` : 'Ask Talab anything...'} className="w-full resize-none outline-none text-base lg:text-lg text-slate-800 placeholder:text-slate-300 bg-transparent min-h-[44px]" rows={1} data-id="element-3420" data-mp="q09n1le91f8f" />

                    {/* Attachment chips */}
                    {attachments.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-3421" data-mp="1npbesukyb6hw">
                        {attachments.map((name, i) => <span key={i} className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 text-slate-600 text-xs font-medium pl-3 pr-1.5 py-1.5 rounded-full" data-id="element-3422" data-mp="26d3s7ddox5mv" data-mpi={i}>
                            <PaperclipIcon className="w-3.5 h-3.5" data-id="element-3423" data-mp="229xly5qkvege" />
                            <span className="max-w-[140px] truncate" data-id="element-3424" data-mp="1nmck0f5fvla2">
                              {name}
                            </span>
                            <button onClick={() => removeAttachment(i)} className="p-0.5 hover:bg-slate-200 rounded-full" aria-label={`Remove ${name}`} data-id="element-3425" data-mp="1sdksn8yy32m6">
                              <XIcon className="w-3.5 h-3.5" data-id="element-3426" data-mp="14mykzisurkcp" />
                            </button>
                          </span>)}
                      </div>}

                    <div className="flex items-center justify-between mt-2" data-id="element-3427" data-mp="1p4kxnw5h24pj">
                      {/* Verb Row */}
                      <div className="flex items-center gap-3 text-[11px] font-semibold text-slate-400 uppercase tracking-widest hidden sm:flex" data-id="element-3428" data-mp="1k6jc94kkptaw">
                        <span data-id="element-3429" data-mp="1b4jx6ht4czal">Find</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-3430" data-mp="1b3i9ndux2kbz" />
                        <span data-id="element-3431" data-mp="1660a2e2shhgn">Buy</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-3432" data-mp="1671ieasf7puo" />
                        <span data-id="element-3433" data-mp="11dp7f3gnzhke">Sell</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-3434" data-mp="11cnlg3y41anw" />
                        <span data-id="element-3435" data-mp="wg6vd47com86">Hire</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-3436" data-mp="se28qpfbkpv1" />
                        <span data-id="element-3437" data-mp="sbz87bgjjec2">Rent</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-3438" data-mp="nfilmlozp813" />
                        <span data-id="element-3439" data-mp="ifw3bxub11lf">Compare</span>
                      </div>

                      <div className="flex items-center gap-1 ml-auto" data-id="element-3440" data-mp="ihz5fg8qel6i">
                        <button type="button" onClick={toggleRecording} aria-label="Voice input" className={`p-2.5 rounded-full transition-colors ${isRecording ? 'text-white bg-error animate-pulse' : 'text-slate-400 hover:text-primary-indigo hover:bg-slate-50'}`} data-id="element-3441" data-mp="2u2g6dzkl9k2d">
                          <MicIcon className="w-5 h-5" data-id="element-3442" data-mp="2be1ezb6d0411" />
                        </button>
                        <button type="button" onClick={() => imageInputRef.current?.click()} aria-label="Attach image" className="p-2.5 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors" data-id="element-3443" data-mp="2gcw7x5y1j8yv">
                          <ImageIcon className="w-5 h-5" data-id="element-3444" data-mp="1skflykb6mwua" />
                        </button>
                        <button type="button" onClick={() => docInputRef.current?.click()} aria-label="Attach file" className="p-2.5 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors" data-id="element-3445" data-mp="1wlheyjeiacnt">
                          <PaperclipIcon className="w-5 h-5" data-id="element-3446" data-mp="py7zlwx6p5xk" />
                        </button>
                        <button onClick={() => handleSubmit()} disabled={!query.trim() && attachments.length === 0} className="ml-2 p-3.5 bg-primary-indigo text-white rounded-full hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" data-id="element-3447" data-mp="uv7xhkjvr2dl">
                          <ArrowUpIcon className="w-5 h-5" data-id="element-3448" data-mp="35ou72iufq9f4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Example Prompts */}
                <div className="w-full max-w-5xl mb-8 z-10 relative" data-id="element-3449" data-mp="334o5uix7gx29">
                  <div className="flex items-center justify-center gap-4 mb-5" data-id="element-3450" data-mp="2yco36ipr3q1z">
                    <div className="h-px bg-slate-100 flex-1 max-w-[40px]" data-id="element-3451" data-mp="2tdtjlejylxgt" />
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400" data-id="element-3452" data-mp="2tc073imo0j0w">
                      Try asking something like
                    </p>
                    <div className="h-px bg-slate-100 flex-1 max-w-[40px]" data-id="element-3453" data-mp="3m28lcze2yja7" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-3454" data-mp="3h470c8x3g9w3">
                    {suggestedPrompts.map((prompt, index) => {
                  const Icon = prompt.icon;
                  return <motion.button key={index} initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 0.2 + index * 0.05
                  }} onClick={() => handlePromptClick(prompt.text)} className="flex items-center justify-between px-4 py-3 bg-white border border-slate-100 rounded-2xl hover:border-slate-200 hover:bg-slate-50/50 transition-all text-left group shadow-sm" data-id="element-3455" data-mp="20pfjmt9b8r2m" data-mpi={index}>
                          <div className="flex items-center gap-3" data-id="element-3456" data-mp="1k71eu2kjhf5m">
                            <div className="p-2 bg-slate-50 rounded-lg group-hover:bg-white transition-colors" data-id="element-3457" data-mp="15hvgegtizsys">
                              <Icon className="w-4 h-4 text-primary-indigo/60" data-id="element-3458" data-mp="1adar4zw8l8qz" />
                            </div>
                            <span className="text-[13px] font-medium text-slate-600 leading-snug pr-2" data-id="element-3459" data-mp="wi005xl457rw">
                              {prompt.text}
                            </span>
                          </div>
                          <ChevronRightIcon className="w-4 h-4 text-slate-300 group-hover:text-primary-indigo/50 transition-colors flex-shrink-0" data-id="element-3460" data-mp="cu3t9xsmrxfd" />
                        </motion.button>;
                })}
                  </div>
                </div>
              </motion.div>
            </div> :
        // Chat History State
        <div className="space-y-6 pb-36 pt-2" data-id="element-3461" data-mp="10pycjd9apskj">
              {chatHistory.map((msg, idx) => <div key={idx} data-id="element-3462" data-mp="vqltz4pgj7nr" data-mpi={idx}>
                  {msg.role === 'user' ? <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex justify-end mb-6" data-id="element-3463" data-mp="19jl7x29lctt7">
                      <div className="bg-slate-100 rounded-2xl rounded-tr-sm px-5 py-3 max-w-[80%] shadow-sm" data-id="element-3464" data-mp="1ylu0yh55an81">
                        {msg.vehicles && msg.vehicles.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-3465" data-mp="27dm4e7vr4w34">
                            {msg.vehicles.map(v => <span key={v.id} className="inline-flex items-center gap-1.5 bg-white border border-primary-indigo/20 text-primary-indigo text-xs font-medium px-3 py-1.5 rounded-full" data-id="element-3466" data-mp="2ccyud1hx6j9x">
                                <SparklesIcon className="w-3.5 h-3.5" data-id="element-3467" data-mp="1omv19o2cy1x6" />
                                {v.title}
                              </span>)}
                          </div>}
                        <p className="text-slate-800 text-[15px]" data-id="element-3468" data-mp="301rvdcdefhbe">
                          {msg.content}
                        </p>
                      </div>
                    </motion.div> : <div className="mb-6" data-id="element-3469" data-mp="tg3spe89c2v4">
                      <AIResponse message={msg.content} insights={msg.kind === 'search' ? msg.category === 'property' ? propertyInsights : mockInsights : undefined} onInsightClick={scrollToListing} data-id="element-3470" data-mp="oeo8tphtg2eb" />

                      {msg.kind === 'seller_offer' && <div className="flex flex-col sm:flex-row gap-3 mt-4 ml-0 md:ml-14" data-id="element-3471" data-mp="q52vgm8o1pe">
                          <button onClick={() => createBusinessHub(msg.sellType ?? 'generic')} className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-primary-indigo to-ai-purple text-white rounded-xl font-semibold shadow-sm hover:brightness-110 transition-all" data-id="element-3472" data-mp="3rwpx197lbpqn">
                            <BriefcaseIcon className="w-5 h-5" data-id="element-3473" data-mp="3pdbddg74ial2" />
                            Yes, create Business Hub
                          </button>
                          <button onClick={continueWithOneListing} className="inline-flex items-center justify-center px-5 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:border-primary-indigo hover:text-primary-indigo transition-colors" data-id="element-3474" data-mp="3fmgumlt35t4d">
                            Continue with one listing
                          </button>
                        </div>}

                      {msg.kind === 'narrowed' ?
              // Narrowed: 3 strong matches
              <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.4
              }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5 ml-0 md:ml-12" data-id="element-3475" data-mp="2mxt68hd1inml">
                          {(msg.category === 'property' ? propertyNarrowed : narrowedListings).map((listing, lIdx) => <motion.div key={listing.id} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.5 + lIdx * 0.1
                }} data-id="element-3476" data-mp="26dlwcftb0zjp" data-mpi={lIdx}>
                              <ListingCard {...withSelection(listing)} data-id="element-3477" data-mp="xc9jinkl006r" />
                            </motion.div>)}
                        </motion.div> : msg.kind === 'search' ?
              // Broad search: swipeable carousel
              <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.4
              }} className="mt-6 ml-0 md:ml-14" data-id="element-3478" data-mp="1bbsr9f9hd9ij">
                          <div className="flex items-center justify-between mb-3 pr-4" data-id="element-3479" data-mp="1u3btvkzlelk1">
                            <span className="text-sm font-medium text-slate-500" data-id="element-3480" data-mp="3ekkyv08rz8zs">
                              {msg.category === 'property' ? 'Showing top matches of 18' : 'Showing top matches of 27'}
                            </span>
                            <span className="text-xs text-slate-400 hidden md:inline" data-id="element-3481" data-mp="30lbhvycqwaur">
                              {msg.category === 'property' ? 'Tap "Ask AI" on a home to ask about it →' : 'Tap "Ask AI" on a car to ask about it →'}
                            </span>
                          </div>
                          <ListingCarousel listings={(msg.category === 'property' ? propertyListings : mockListings).map(withSelection)} data-id="element-3482" data-mp="3jjfghyv0arib" />
                        </motion.div> : null}
                    </div>}
                </div>)}

              {isSearching && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="flex gap-4" data-id="element-3483" data-mp="3wp2yv8twy2n">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-md" data-id="element-3484" data-mp="quto73ocew8c">
                    <SparklesIcon className="w-5 h-5 text-white animate-pulse" data-id="element-3485" data-mp="mrxczy14u2ef" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-none p-5 shadow-soft border border-slate-100 flex items-center gap-2" data-id="element-3486" data-mp="10mzfeb6rt9mh">
                    <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-3487" data-mp="1aijc2zt5scca" />
                    <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-3488" data-mp="1p6t0aan1ne8m" />
                    <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-3489" data-mp="1k6okkmtymnjg" />
                  </div>
                </motion.div>}
              {/* Scroll anchor — keeps newest message + input in view */}
              <div ref={bottomRef} className="h-px" data-id="element-3490" data-mp="wfj7ugjbgbdd" />
            </div>}
        </div>
      </div>

      {/* Floating Input (always visible while chatting) */}
      {(hasResults || isSearching || chatHistory.length > 0) && <>
          {/* Soft fade so content dissolves behind the floating pill */}
          <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none z-20" data-id="element-3491" data-mp="8e62bp1gfiu9" />
          <div className="absolute bottom-5 left-0 right-0 px-4 lg:px-8 z-30 pointer-events-none flex justify-center" data-id="element-3492" data-mp="3lmnypuy0joe">
            <div className="w-full max-w-3xl pointer-events-auto" data-id="element-3493" data-mp="3uqw65vcp3tng">
              {/* Selected vehicle tags */}
              {selectedVehicles.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex flex-wrap items-center gap-2 mb-3 px-1" data-id="element-3494" data-mp="rb7afiqb1z2f">
                  <span className="text-xs font-medium text-slate-500 mr-1" data-id="element-3495" data-mp="chlv2aaekz1o">
                    Asking about:
                  </span>
                  {selectedVehicles.map(v => <span key={v.id} className="inline-flex items-center gap-1.5 bg-white border border-primary-indigo/30 text-primary-indigo text-xs font-medium pl-3 pr-2 py-1.5 rounded-full shadow-sm" data-id="element-3496" data-mp="2x7l1ugfpp22p">
                      <SparklesIcon className="w-3.5 h-3.5" data-id="element-3497" data-mp="1mku1c8jrse7t" />
                      {v.title}
                      <button onClick={() => toggleVehicle(v.id, v.title)} aria-label={`Remove ${v.title}`} className="ml-0.5 p-0.5 hover:bg-primary-indigo/10 rounded-full transition-colors" data-id="element-3498" data-mp="1rddnhtwiwho8">
                        <XIcon className="w-3.5 h-3.5" data-id="element-3499" data-mp="25btwjrhuyk9j" />
                      </button>
                    </span>)}
                </motion.div>}
              {/* Attachment chips */}
              {attachments.length > 0 && <div className="flex flex-wrap items-center gap-2 mb-3 px-1" data-id="element-3500" data-mp="b0f596978cro">
                  {attachments.map((name, i) => <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-medium pl-3 pr-1.5 py-1.5 rounded-full shadow-sm" data-id="element-3501" data-mp="26dr8gpj3q2j6" data-mpi={i}>
                      <PaperclipIcon className="w-3.5 h-3.5" data-id="element-3502" data-mp="2fcuthz1tws0b" />
                      <span className="max-w-[140px] truncate" data-id="element-3503" data-mp="2beosl7no96nb">{name}</span>
                      <button onClick={() => removeAttachment(i)} className="p-0.5 hover:bg-slate-100 rounded-full" aria-label={`Remove ${name}`} data-id="element-3504" data-mp="1nn9md3sry56s">
                        <XIcon className="w-3.5 h-3.5" data-id="element-3505" data-mp="1whe7r7p52oex" />
                      </button>
                    </span>)}
                </div>}
              <div className="w-full bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100 p-2 relative group transition-all" data-id="element-3506" data-mp="13yqqk3d9jtj4">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-indigo/20 to-ai-purple/20 rounded-3xl blur opacity-0 group-focus-within:opacity-100 transition duration-500" data-id="element-3507" data-mp="zwdvqa021g14" />
                <div className="relative bg-white rounded-3xl flex items-end gap-2 px-4 py-2" data-id="element-3508" data-mp="umtcku2rbagr">
                  <button type="button" onClick={() => docInputRef.current?.click()} aria-label="Attach file" className="p-2 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors flex-shrink-0 mb-0.5" data-id="element-3509" data-mp="ftzncgxonfyw">
                    <PaperclipIcon className="w-5 h-5" data-id="element-3510" data-mp="yn3i65mmzyv3" />
                  </button>
                  <textarea value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }} rows={1} placeholder={selectedVehicles.length > 0 ? `Ask about ${selectedVehicles.length > 1 ? 'these vehicles' : 'this vehicle'}...` : 'Ask Talab anything...'} className="flex-1 bg-transparent outline-none text-slate-800 placeholder:text-slate-400 resize-none py-2 max-h-32 leading-relaxed" data-id="element-3511" data-mp="13ler8e53y5c9" />
                  <button type="button" onClick={toggleRecording} aria-label="Voice input" className={`p-2 rounded-full transition-colors flex-shrink-0 mb-0.5 ${isRecording ? 'text-white bg-error animate-pulse' : 'text-slate-400 hover:text-primary-indigo hover:bg-slate-50'}`} data-id="element-3512" data-mp="173cj6p4w8p47">
                    <MicIcon className="w-5 h-5" data-id="element-3513" data-mp="2rjp99g606434" />
                  </button>
                  <button onClick={() => handleSubmit()} disabled={!query.trim() && selectedVehicles.length === 0 && attachments.length === 0 || isSearching} className="p-2.5 bg-primary-indigo text-white rounded-full hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex-shrink-0 mb-0.5" data-id="element-3514" data-mp="2rhcxxlur2rz1">
                    <ArrowUpIcon className="w-5 h-5" data-id="element-3515" data-mp="2l2nu4d0o90a6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}